import React, { useState, useEffect } from 'react';
import { transactionAPI, userAPI } from '../../services/api';
import { authAPI } from '../../services/api';
import './TransactionHistory.css';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [filter, setFilter] = useState('all'); // all, credit, debit
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = authAPI.getCurrentUser();
        if (!user) return;

        // Fetch user accounts
        const accountsData = await userAPI.getUserAccounts(user.id);
        setAccounts(accountsData.accounts || []);

        // Select first account by default
        if (accountsData.accounts && accountsData.accounts.length > 0) {
          setSelectedAccount(accountsData.accounts[0].id);
        }
      } catch (err) {
        setError('Failed to load accounts');
        console.error(err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
    }
  }, [selectedAccount]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await transactionAPI.getTransactions({ accountId: selectedAccount });
      setTransactions(data.transactions || []);
      setError('');
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    return t.transaction_type === filter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, type) => {
    const sign = type === 'credit' ? '+' : '-';
    return `${sign}$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="transaction-history">
      <div className="transaction-header">
        <h2>Transaction History</h2>
        <div className="transaction-controls">
          <select
            className="input account-select"
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <option value="">Select Account</option>
            {accounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.account_type.toUpperCase()} - {account.account_number}
              </option>
            ))}
          </select>

          <div className="filter-buttons">
            <button
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`btn ${filter === 'credit' ? 'btn-success' : 'btn-secondary'}`}
              onClick={() => setFilter('credit')}
            >
              Credits
            </button>
            <button
              className={`btn ${filter === 'debit' ? 'btn-danger' : 'btn-secondary'}`}
              onClick={() => setFilter('debit')}
            >
              Debits
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="transaction-table-container">
          {filteredTransactions.length === 0 ? (
            <div className="no-transactions">
              <p>No transactions found</p>
            </div>
          ) : (
            <table className="transaction-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map(transaction => (
                  <tr key={transaction.id} className={`transaction-row ${transaction.transaction_type}`}>
                    <td className="transaction-date">{formatDate(transaction.transaction_date)}</td>
                    <td className="transaction-description">{transaction.description || 'N/A'}</td>
                    <td className="transaction-category">
                      <span className="category-badge">{transaction.category || 'Other'}</span>
                    </td>
                    <td className="transaction-type">
                      <span className={`type-badge ${transaction.transaction_type}`}>
                        {transaction.transaction_type.toUpperCase()}
                      </span>
                    </td>
                    <td className={`transaction-amount ${transaction.transaction_type}`}>
                      {formatAmount(transaction.amount, transaction.transaction_type)}
                    </td>
                    <td className="transaction-status">
                      <span className={`status-badge ${transaction.status}`}>
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="transaction-summary">
        <p>Total Transactions: <strong>{filteredTransactions.length}</strong></p>
      </div>
    </div>
  );
};

export default TransactionHistory;
